import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../utils.ts';
import { Menu, X, Search, Bell, Settings, ChevronDown } from 'lucide-react';
import NavbarContent from '@/lib/components/common/navbar/NavbarContent.tsx';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

// Excluded paths where navbar should not show
const excludedPaths = [
  '/',
  '/login',
  '/register',
  '/auth/action',
  '/en',
  '/organizations/create',
  '/organization/create',
  '/organizations',
  '/welcome',
  '/setup/teacher',
  '/setup/student',
  '/setup/parent',
  '/verification',
];

// Utility function to check if navbar should be excluded for a given path
export const shouldExcludeNavbar = (pathname: string): boolean => {
  return excludedPaths.includes(pathname) || pathname.startsWith('/nfc/') || pathname.startsWith('/qr/') || pathname.startsWith('/admin');
};

// Page titles mapping
const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Monitor your energy systems and performance' },
  '/files': { title: 'Files', subtitle: 'Manage documents and files' },
  '/personnel': { title: 'Personnel', subtitle: 'Manage team members and permissions' },
  '/settings': { title: 'Settings', subtitle: 'Configure your account and preferences' },
  '/chart-config': { title: 'Chart Config', subtitle: 'Configure your charts' },
  '/naeras': { title: 'NAERAS Υβριδικό', subtitle: 'Διαχείριση προσφορών και επισκόπηση' },
  '/naeras/hybrid': { title: 'NAERAS Hybrid Feature', subtitle: 'Υπολογισμός και αναθεώρηση υβριδικών προσφορών' },
  '/history': { title: 'Ιστορικό Προσφορών', subtitle: 'Προβολή και εξαγωγή ιστορικού προσφορών ενέργειας' },
  '/job-runs': { title: 'Κατάσταση Εργασιών', subtitle: 'Παρακολούθηση των αυτόματων εργασιών NAERAS' },
};

import { useSelector } from 'react-redux';
import { getCurrentSessionUser, getCurrentOrganization } from '@/data/store/selectors';

const Navbar = () => {
  const { pathname } = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const currentUser = useSelector(getCurrentSessionUser) as any;
  const organization = useSelector(getCurrentOrganization) as any;

  if (shouldExcludeNavbar(pathname)) {
    return null;
  }

  const currentPage = pageTitles[pathname] || { title: 'Page', subtitle: 'Welcome to Enexan' };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <>
      {/* Main Navbar */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="pt-4"
      >
        <div className="flex h-16 items-center justify-between px-6">
          {/* Left Section - Menu and Page Info */}
          <div className="flex items-center gap-6">
            {/* Menu Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleDropdown}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50"
            >
              {isDropdownOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </motion.button>

            {/* Page Title */}
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{currentPage.title}</h1>
              <p className="text-sm text-gray-500">{currentPage.subtitle}</p>
            </div>
          </div>

          {/* Center Section - Search */}
          <div className="hidden rounded-xl shadow-lg md:block">
            <div className="relative">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-80 rounded-xl border border-gray-200 bg-white py-2.5 pr-4 pl-10 text-sm text-gray-900 placeholder-gray-500 transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
              />
              <div className="absolute top-1/2 right-3 -translate-y-1/2">
                <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">⌘K</kbd>
              </div>
            </div>
          </div>

          {/* Right Section - Actions and Profile */}
          <div className="flex h-12 items-center gap-3">
            {/* Notifications */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative flex h-12 w-12 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50"
              style={{ minWidth: '3rem', minHeight: '3rem' }}
            >
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white">
                3
              </span>
            </motion.button>

            {/* Settings */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/settings')}
              className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50"
              style={{ minWidth: '3rem', minHeight: '3rem' }}
            >
              <Settings className="h-5 w-5" />
            </motion.button>

            {/* Profile */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex h-12 items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm transition-all hover:cursor-pointer hover:border-gray-300 hover:bg-gray-50"
              style={{ minHeight: '3rem' }}
              onClick={() => {
                navigate('/profile');
              }}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                {currentUser?.avatar || currentUser?.image_url ? (
                  <img src={currentUser.avatar || currentUser.image_url} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs font-semibold text-white">
                    {currentUser?.first_name?.charAt(0)}{currentUser?.last_name?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="hidden lg:block">
                <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                  {currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() : 'User'}
                </div>
                <div className="text-xs leading-tight text-gray-400 truncate max-w-[150px]">
                  {currentUser?.email || 'N/A'}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.nav>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isDropdownOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={toggleDropdown}
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            />

            {/* Dropdown Content */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="fixed top-20 left-6 z-50 w-80 rounded-2xl border border-gray-200 bg-white/95 p-6 shadow-xl backdrop-blur-md"
            >
              <NavbarContent onItemClick={toggleDropdown} pathname={pathname} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
